import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components/macro'
import { useTranslation } from 'react-i18next'

import Button from 'components/atoms/button'
import Text from 'components/atoms/text'
import LanguagePicker from 'components/molecules/language-picker'

const Container = styled.div`
  width: 100%;
`

const Card = styled.div`
  box-shadow: ${({ theme }) => theme.shadows.bottom};
  background-color: ${({ theme }) => theme.colors.brandGolf};
  padding: ${({ theme }) => theme.sizings.lvl3};
`

const Header = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.sizeByFactor(1.5)};

  /* The MUI Select used by LanguagePicker reserves an invisible label slot
     above the visible flag; anchor the picker to the heading's baseline. */
  > *:last-child {
    margin-bottom: ${({ theme }) => theme.sizings.lvl0};
  }

  /* Override the global RDC arrow position (right: 10px) for the language
     picker on the login page — the tiny flag dropdown needs the arrow tighter
     to the edge to look balanced. */
  .MuiSelect-icon,
  .MuiSelect-iconFilled,
  .MuiSelect-iconOutlined {
    right: 3px;
  }
`

const Description = styled(Text)`
  display: block;
  margin-bottom: ${({ theme }) => theme.sizeByFactor(2.5)};
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
`

const StyledButton = styled(Button)`
  margin: 0 auto;
`

/**
 * Login card for tenants that authenticate through an external OIDC provider
 * (e.g. RDC via Mobi ID). Mirrors the visual structure of LoginForm but
 * replaces the credentials form with a description + a single sign-in button.
 */
function OidcLoginCard({ buttonText, description, onSignIn }) {
  const { t } = useTranslation()

  return (
    <Container>
      <Card>
        <Header>
          <Text type="h1" text={t('login')} />
          <LanguagePicker />
        </Header>
        <Description type="body" text={description} />
        <ButtonRow>
          <StyledButton level="cta" size="large" onClick={onSignIn}>
            {buttonText}
          </StyledButton>
        </ButtonRow>
      </Card>
    </Container>
  )
}

OidcLoginCard.propTypes = {
  buttonText: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onSignIn: PropTypes.func.isRequired,
}

export default OidcLoginCard
